/**
 * Create or edit a WhatsApp template.
 *
 * The variable list is DERIVED from the copy, never typed separately — writing {{patient_name}} in
 * the body is what creates the variable. That keeps the placeholders and the declared variables in
 * lockstep, which is exactly what Meta rejects a submission for getting wrong.
 *
 * Once Meta approves a template it is frozen there, so this modal drops to bindings-only editing
 * rather than letting the local copy silently drift from what actually sends.
 */
import React, { useMemo, useState } from 'react';
import { X, Send, Save, AlertTriangle, Lock, Wand2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useGetCrmTokensQuery,
  useCreateWhatsAppTemplateMutation,
  useUpdateWhatsAppTemplateMutation,
  useUpdateTemplateBindingsMutation,
  useSubmitWhatsAppTemplateMutation,
} from '../../api/campaignsApi';

const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

// Meta's own caps — mirrored here so the counter turns red before a submission is wasted.
const LIMITS = { header: 60, body: 1024, footer: 60 };

const parseVars = (text) => {
  const out = [];
  for (const m of String(text || '').matchAll(PLACEHOLDER)) {
    if (!out.includes(m[1])) out.push(m[1]);
  }
  return out;
};

const apiError = (e) =>
  e?.data?.error?.message
  || Object.values(e?.data?.error?.errors ?? {}).flat()[0]
  || 'Something went wrong.';

export default function TemplateEditorModal({ template, onClose }) {
  const isNew = !template;
  // Approved/Pending templates are frozen at Meta — only bindings remain editable.
  const locked = !isNew && !['Draft', 'Rejected'].includes(template.status);

  const [form, setForm] = useState(() => ({
    name: template?.name ?? '',
    languageCode: template?.languageCode ?? 'en',
    category: template?.category ?? 'Utility',
    headerText: template?.headerText ?? '',
    bodyText: template?.bodyText ?? '',
    footerText: template?.footerText ?? '',
  }));

  // Templates imported from a Meta sync start disabled on purpose — see WhatsAppTemplateService —
  // so this is the control that actually arms one. Without it a synced/approved template can never
  // be picked by an automation, however correctly its variables are bound.
  const [isActive, setIsActive] = useState(template?.isActive ?? true);

  // Per-variable binding/example state, keyed by "Component:name" so it survives edits to the copy.
  const [meta, setMeta] = useState(() => {
    const seed = {};
    (template?.variables ?? []).forEach((v) => {
      seed[`${v.component}:${v.variableName}`] = {
        id: v.id,
        bindingToken: v.bindingToken ?? '',
        fallbackText: v.fallbackText ?? '',
        exampleValue: v.exampleValue ?? '',
        suggested: v.suggestedBindingToken ?? '',
      };
    });
    return seed;
  });

  // Variables the server could confidently infer a field for. Applied only on an explicit click —
  // a wrong guess would put the wrong text in front of a patient.
  const suggestable = Object.entries(meta)
    .filter(([, m]) => !m.bindingToken && m.suggested)
    .map(([key]) => key);

  const applySuggestions = () => {
    setMeta((m) => {
      const next = { ...m };
      suggestable.forEach((key) => {
        next[key] = {
          ...next[key],
          bindingToken: next[key].suggested,
          exampleValue: next[key].exampleValue || tokenExample(next[key].suggested) || '',
        };
      });
      return next;
    });
  };

  const { data: tokens } = useGetCrmTokensQuery();
  const [create, { isLoading: creating }] = useCreateWhatsAppTemplateMutation();
  const [update, { isLoading: updating }] = useUpdateWhatsAppTemplateMutation();
  const [saveBindings, { isLoading: savingBindings }] = useUpdateTemplateBindingsMutation();
  const [submit, { isLoading: submitting }] = useSubmitWhatsAppTemplateMutation();

  const headerVars = useMemo(() => parseVars(form.headerText), [form.headerText]);
  const bodyVars = useMemo(() => parseVars(form.bodyText), [form.bodyText]);
  const allVars = useMemo(
    () => [
      ...headerVars.map((n, i) => ({ component: 'Header', variableName: n, ordinal: i })),
      ...bodyVars.map((n, i) => ({ component: 'Body', variableName: n, ordinal: i })),
    ],
    [headerVars, bodyVars]
  );

  const at = (v) => meta[`${v.component}:${v.variableName}`] ?? {};
  const setAt = (v, patch) =>
    setMeta((m) => ({ ...m, [`${v.component}:${v.variableName}`]: { ...at(v), ...patch } }));

  const tokenExample = (key) => tokens?.find((t) => t.key === key)?.example;

  // Live preview: prefer the author's own example, fall back to the bound field's sample value.
  const preview = (text, vars) =>
    String(text || '').replace(PLACEHOLDER, (_, name) => {
      const v = vars.find((x) => x.variableName === name);
      const m = v ? at(v) : {};
      return m.exampleValue || tokenExample(m.bindingToken) || `{{${name}}}`;
    });

  const missingExamples = allVars.filter((v) => !at(v).exampleValue?.trim());
  const footerHasVars = parseVars(form.footerText).length > 0;

  const canSave =
    form.name.trim() && form.bodyText.trim() && !footerHasVars
    && form.bodyText.length <= LIMITS.body
    && (form.headerText || '').length <= LIMITS.header
    && (form.footerText || '').length <= LIMITS.footer;

  const buildPayload = () => ({
    ...form,
    headerText: form.headerText?.trim() || null,
    footerText: form.footerText?.trim() || null,
    variables: allVars.map((v) => ({
      component: v.component,
      variableName: v.variableName,
      ordinal: v.ordinal,
      bindingToken: at(v).bindingToken || null,
      fallbackText: at(v).fallbackText || null,
      exampleValue: at(v).exampleValue || null,
    })),
  });

  const save = async () => {
    try {
      if (locked) {
        await saveBindings({
          id: template.id,
          isActive,
          variables: (template.variables ?? []).map((v) => {
            const m = meta[`${v.component}:${v.variableName}`] ?? {};
            return { id: v.id, bindingToken: m.bindingToken || null, fallbackText: m.fallbackText || null };
          }),
        }).unwrap();
        toast.success('Saved.');
      } else if (isNew) {
        await create(buildPayload()).unwrap();
        toast.success('Template saved as a draft. Submit it when you’re ready for Meta to review.');
      } else {
        await update({ id: template.id, ...buildPayload() }).unwrap();
        // A brand-new (never-synced) template already has isActive defaulted server-side; only
        // push a change here if the user actually touched the toggle.
        if (isActive !== template.isActive) {
          await saveBindings({ id: template.id, isActive, variables: [] }).unwrap();
        }
        toast.success('Template updated.');
      }
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitToMeta = async () => {
    if (isNew) { toast.info('Save the template first, then submit it.'); return; }
    try {
      const r = await submit(template.id).unwrap();
      if (!r.success) { toast.error(r.error || 'Meta rejected the submission.'); return; }
      toast.success('Submitted to Meta. Approval usually takes a few minutes, sometimes up to 24 hours.');
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const busy = creating || updating || savingBindings || submitting;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isNew ? 'New WhatsApp template' : template.name}</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body" style={{ display: 'grid', gap: 14 }}>
          {locked && (
            <div className="maintenance-banner" role="status">
              <Lock size={16} />
              <span>
                <strong>This template is {template.status} at Meta.</strong> Approved templates are frozen
                there, so the wording can’t be changed — only which CRM field each variable pulls from.
                To change the wording, create a new template.
              </span>
            </div>
          )}

          {!isNew && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span style={{ fontSize: '0.85rem' }}>Enabled — usable in automations and the inbox</span>
            </label>
          )}
          {!isNew && !isActive && (
            <div className="td-muted" style={{ fontSize: '0.76rem', marginTop: -8 }}>
              Templates pulled in by "Sync from Meta" start disabled. Nothing can send this until you
              tick the box above and save — an automation pointed at a disabled template just won't
              show it as an option.
            </div>
          )}

          {!locked && (
            <>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
                <div>
                  <label className="form-label">Template name</label>
                  <input
                    className="form-control td-mono"
                    placeholder="appointment_reminder"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  />
                  <div className="td-muted" style={{ fontSize: '0.72rem', marginTop: 3 }}>
                    Lowercase letters, numbers and underscores only.
                  </div>
                </div>
                <div>
                  <label className="form-label">Language</label>
                  <input className="form-control" value={form.languageCode}
                    onChange={(e) => setForm({ ...form, languageCode: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Category</label>
                  <select className="form-control" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="Utility">Utility</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Authentication">Authentication</option>
                  </select>
                </div>
              </div>

              {form.category === 'Marketing' && (
                <div className="td-muted" style={{ fontSize: '0.78rem', display: 'flex', gap: 6 }}>
                  <AlertTriangle size={13} />
                  Marketing templates cost more per conversation, take longer to review, and count against
                  your quality rating if people block you. Use Utility for anything transactional.
                </div>
              )}

              <Field label="Header (optional)" hint="Shown in bold above the message."
                value={form.headerText} limit={LIMITS.header}
                onChange={(v) => setForm({ ...form, headerText: v })} />

              <Field label="Message body" textarea rows={5}
                hint="Type {{variable_name}} anywhere to insert a value from the CRM."
                value={form.bodyText} limit={LIMITS.body}
                onChange={(v) => setForm({ ...form, bodyText: v })} />

              <Field label="Footer (optional)" hint="Small print. Cannot contain variables."
                value={form.footerText} limit={LIMITS.footer}
                error={footerHasVars ? 'Footers cannot contain variables.' : null}
                onChange={(v) => setForm({ ...form, footerText: v })} />
            </>
          )}

          {allVars.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                <div className="td-bold" style={{ fontSize: '0.74rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Variables — link each one to a CRM field
                </div>
                {suggestable.length > 0 && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={applySuggestions}>
                    <Wand2 size={13} /> Fill in {suggestable.length} suggestion{suggestable.length === 1 ? '' : 's'}
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {allVars.map((v) => {
                  const m = at(v);
                  return (
                    <div key={`${v.component}:${v.variableName}`}
                      style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 8, alignItems: 'center' }}>
                      <div className="td-mono" style={{ fontSize: '0.8rem' }}>
                        {`{{${v.variableName}}}`}
                        {v.component === 'Header' && <span className="badge b-gray" style={{ marginLeft: 4 }}>header</span>}
                      </div>
                      <select className="form-control" value={m.bindingToken ?? ''}
                        onChange={(e) => {
                          const key = e.target.value;
                          // Seed the example from the chosen field so submission isn't blocked on
                          // typing one out by hand.
                          setAt(v, { bindingToken: key, exampleValue: m.exampleValue || tokenExample(key) || '' });
                        }}>
                        <option value="">
                          {m.suggested ? `— not linked (suggested: ${m.suggested}) —` : '— not linked —'}
                        </option>
                        {(tokens ?? []).map((t) => (
                          <option key={t.key} value={t.key}>{t.label}{t.scope ? ` (${t.scope})` : ''}</option>
                        ))}
                      </select>
                      <input className="form-control" placeholder="Example value (required by Meta)"
                        value={m.exampleValue ?? ''}
                        onChange={(e) => setAt(v, { exampleValue: e.target.value })} />
                    </div>
                  );
                })}
              </div>
              {missingExamples.length > 0 && (
                <div className="td-muted" style={{ fontSize: '0.78rem', marginTop: 8, display: 'flex', gap: 6 }}>
                  <AlertTriangle size={13} />
                  Meta rejects a submission unless every variable has an example value.
                </div>
              )}
            </div>
          )}

          <div>
            <div className="td-mono td-bold" style={{ fontSize: '0.74rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Preview
            </div>
            <div className="card card-body" style={{ background: 'var(--bg-hover)' }}>
              {form.headerText && <div className="td-bold" style={{ marginBottom: 6 }}>{preview(form.headerText, allVars)}</div>}
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.86rem' }}>{preview(form.bodyText, allVars) || <span className="td-muted">Your message will appear here.</span>}</div>
              {form.footerText && <div className="td-muted" style={{ fontSize: '0.76rem', marginTop: 6 }}>{form.footerText}</div>}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || (!locked && !canSave)}>
            <Save size={15} /> {locked ? 'Save' : 'Save draft'}
          </button>
          {!isNew && ['Draft', 'Rejected'].includes(template.status) && (
            <button className="btn btn-primary" onClick={submitToMeta} disabled={busy || missingExamples.length > 0}>
              <Send size={15} /> Submit to Meta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, value, onChange, limit, textarea, rows, error }) {
  const over = (value || '').length > limit;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <label className="form-label">{label}</label>
        <span className="td-muted" style={{ fontSize: '0.72rem', color: over ? 'var(--danger)' : undefined }}>
          {(value || '').length}/{limit}
        </span>
      </div>
      {textarea
        ? <textarea className="form-control" rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
        : <input className="form-control" value={value} onChange={(e) => onChange(e.target.value)} />}
      {(error || hint) && (
        <div className="td-muted" style={{ fontSize: '0.72rem', marginTop: 3, color: error ? 'var(--danger)' : undefined }}>
          {error || hint}
        </div>
      )}
    </div>
  );
}
